import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Play, Star, Clock, TrendingUp, ShieldCheck } from "lucide-react";
import { SEO } from "../components/SEO";
import { RevealText } from "../components/RevealText";
import { EmailOptInForm } from "../components/EmailOptInForm";
import { TOOLS } from "../data/tools";
import { USE_CASES } from "../data/useCases";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { HOME_PAGE } from "../constants/testIds";
import { getOutboundProps, resolveOutbound } from "../config/affiliateLinks";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] },
};

const FAQS = [
  { q: "Are these AI writing tool reviews sponsored?", a: "No review is written or edited by the tool vendors. This site earns affiliate commissions if you sign up through our links, which we disclose clearly on every review page — but our testing scores and rankings are independent." },
  { q: "How do you actually test each tool?", a: "We run the same test prompts through every tool, score outputs on a 100-point rubric, and use each tool for real freelance/marketing work for 12-21 days before publishing." },
  { q: "Which AI writing tool is best for beginners?", a: "Rytr's free plan has the shortest learning curve. If you're willing to invest 20 minutes in brand voice setup, Phrasly.ai gives the best overall quality for the price. If you're writing a full book or guide, Codex Leo AI's free 10,000 tokens is the place to start." },
  { q: "Can AI-written copy hurt my SEO or get flagged as spam?", a: "Search engines don't penalize AI-assisted content for being AI-assisted — they penalize low-quality, unhelpful content, regardless of who or what wrote it. Edited, accurate, genuinely useful AI-assisted copy performs fine." },
  { q: "Do I need more than one AI writing tool?", a: "Most people don't. Pick the tool that matches your primary writing type (see our use-case pages) — Phrasly or Rytr for everyday short-form copy, Codex Leo AI only if you're writing a full book or guide." },
];

const BEFORE = "Introducing our new matcha latte! Made with premium matcha and creamy milk. Try it today and taste the difference. #matcha #latte #newlaunch";
const AFTER = "We spent 8 months sourcing this matcha. Ceremonial-grade, whisked (not blended), finished with oat foam that holds its shape. Today only. First 50 orders free. Link in bio before it's gone. 🍵";

export default function Home() {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const topFive = TOOLS;

  return (
    <>
      <SEO title="Best AI Writing & Copywriting Tools for 2026: Hands-On Comparison" description="A hands-on comparison of the top 3 AI writing and copywriting tools for marketers, freelancers, and content creators — with pricing, real test scores, and honest limitations." />

      {/* HERO */}
      <section className="relative pt-40 pb-24 px-6 lg:px-10 overflow-hidden spotlight">
        <motion.div
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 0.35, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="absolute -top-20 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(212,175,55,0.15), transparent 70%)" }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center">
            <div>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-mono-tag text-xs uppercase tracking-[0.25em] text-gold mb-6">
                16-21 Days Tested · 3 Tools · 100% Independent
              </motion.p>
              <RevealText
                text="Best AI Writing & Copywriting Tools for 2026"
                className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tighter text-white"
              />
              <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.6 }} className="text-lg text-zinc-400 mt-6 max-w-xl leading-relaxed">
                A hands-on comparison — no vendor decks, no guesses. We tested Phrasly.ai, Rytr, and Codex Leo AI on real emails, ads, social posts, and full-length books and guides, and scored every output.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }} className="mt-8">
                <p className="text-xs font-mono-tag uppercase tracking-wider text-zinc-500 mb-3">Get the free AI Writing Prompts Library (20+ templates)</p>
                <EmailOptInForm source="homepage_hero" />
              </motion.div>
            </div>

            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="relative">
              <div className="relative rounded-sm overflow-hidden border border-white/10 aspect-[4/3] group">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85" alt="AI writing tool dashboard comparison in action" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-inkblack/80 via-transparent to-inkblack/20" />
                <button
                  data-testid={HOME_PAGE.heroVideoPlay}
                  onClick={() => setVideoPlaying(true)}
                  className="absolute inset-0 flex items-center justify-center"
                  aria-label="Play tool comparison demo video"
                >
                  <span className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-white fill-white" aria-hidden="true" />
                  </span>
                </button>
                <div className="absolute bottom-4 left-4 font-mono-tag text-xs text-white/70 uppercase tracking-wider">30-sec comparison demo</div>
              </div>
              {videoPlaying && (
                <p className="text-xs text-zinc-500 mt-2 font-mono-tag" data-testid="hero-video-placeholder-note">Demo video coming soon — full written walkthroughs are live in every review.</p>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 py-6 bg-surface/30">
        <Marquee gradient={false} speed={38}>
          {[...TOOLS, ...TOOLS].map((t, i) => (
            <span key={i} className="mx-10 font-display text-2xl text-stroke uppercase tracking-tight">{t.name}</span>
          ))}
        </Marquee>
      </div>

      {/* COMPARISON TABLE */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-3">01 — The Comparison</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight max-w-2xl">3 AI Writing Tools, Scored Side-by-Side</h2>
          </motion.div>
          <motion.div {...fadeUp} className="mt-10 overflow-x-auto scrollbar-hide rounded-sm border border-white/10" data-testid={HOME_PAGE.comparisonTable}>
            <table className="matrix-table w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-surface">
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400 sticky left-0 bg-surface">Tool</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Rating</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Starting Price</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Best For</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400"></th>
                </tr>
              </thead>
              <tbody>
                {topFive.map((tool) => {
                  const outbound = getOutboundProps(tool.affiliateKey);
                  const resolved = resolveOutbound(tool.affiliateKey);
                  const isLive = !!(resolved && resolved.key === tool.affiliateKey);
                  return (
                  <tr key={tool.slug} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-4 sticky left-0 bg-inkblack">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tool.color }} aria-hidden="true" />
                        <span className="text-white font-medium">{tool.name}</span>
                        {tool.editorsChoice && <span className="text-[10px] font-mono-tag uppercase px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/30">Editor's Choice</span>}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-silver flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-gold text-gold inline mr-1" aria-hidden="true" />{tool.rating}</td>
                    <td className="px-5 py-4 text-zinc-300">{tool.pricing[0].price}{tool.pricing[0].period}</td>
                    <td className="px-5 py-4 text-zinc-400">{tool.bestFor}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-4">
                        {isLive && outbound && (
                          <a href={outbound.href} target={outbound.target} rel={outbound.rel} className="text-gold hover:text-white text-sm font-medium inline-flex items-center gap-1 whitespace-nowrap">
                            Try Free <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                          </a>
                        )}
                        <Link to={`/reviews/${tool.slug}`} className="text-primary hover:text-[#5C7CFA] text-sm font-medium inline-flex items-center gap-1 whitespace-nowrap">
                          Read Review <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>
          <motion.div {...fadeUp} className="mt-4 text-right">
            <Link to="/comparison" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1">Open full interactive comparison matrix <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" /></Link>
          </motion.div>
        </div>
      </section>

      {/* USE CASE CARDS */}
      <section className="py-24 px-6 lg:px-10 bg-surface/30 border-y border-white/10">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-3">02 — By Use Case</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight max-w-2xl">Find the Best Tool for What You Actually Write</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {USE_CASES.map((uc, i) => (
              <motion.div
                key={uc.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              >
                <Link to={`/use-cases/${uc.slug}`} data-testid={HOME_PAGE.caseCard(uc.slug)} className="group block h-full border border-white/10 rounded-sm p-6 hover:border-primary/50 transition-colors bg-inkblack">
                  <img src={uc.heroImage} alt={uc.title} className="w-full h-36 object-cover rounded-sm mb-5 grayscale group-hover:grayscale-0 transition-all duration-500" />
                  <h3 className="font-display font-bold text-lg text-white leading-snug">{uc.title}</h3>
                  <p className="text-sm text-zinc-500 mt-2 flex items-center gap-1">Read comparison <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" aria-hidden="true" /></p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-3">03 — Real Output</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight max-w-2xl">See the Difference a Good Prompt Makes</h2>
            <p className="text-zinc-400 mt-4 max-w-xl">Same tool, same product — the only thing that changed was the prompt structure.</p>
          </motion.div>
          <motion.div {...fadeUp} className="grid sm:grid-cols-2 gap-px bg-white/10 rounded-sm overflow-hidden mt-10">
            <div className="bg-surface p-7">
              <span className="text-xs font-mono-tag text-zinc-500 uppercase tracking-widest">Before — generic prompt</span>
              <p className="text-zinc-500 text-base leading-relaxed mt-4">{BEFORE}</p>
            </div>
            <div className="bg-surface p-7 ring-1 ring-gold/20">
              <span className="text-xs font-mono-tag text-gold uppercase tracking-widest">After — R.O.A.S. framework</span>
              <p className="text-silver text-base leading-relaxed mt-4">{AFTER}</p>
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="text-center mt-8">
            <Link to="/guides/prompt-engineering-101" className="text-primary hover:text-[#5C7CFA] text-sm font-medium inline-flex items-center gap-1">Learn the full framework <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" /></Link>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 px-6 lg:px-10 bg-surface/30 border-y border-white/10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8">
          {[
            { icon: Clock, title: "Save 10+ Hours a Week", text: "Our tested workflows cut real writing time by 55-75% across emails, ads, and product copy." },
            { icon: TrendingUp, title: "Data, Not Opinions", text: "Every score in this comparison comes from a documented 100-point testing rubric, run identically across all 5 tools." },
            { icon: ShieldCheck, title: "Honest About Limitations", text: "We publish what each tool is bad at, not just what it's good at — including the tools we're an affiliate for." },
          ].map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.1 }}>
              <b.icon className="w-8 h-8 text-primary mb-4" aria-hidden="true" />
              <h3 className="font-display font-bold text-xl text-white">{b.title}</h3>
              <p className="text-zinc-400 mt-2 leading-relaxed">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-3">04 — FAQ</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight">Questions, Answered Honestly</h2>
          </motion.div>
          <motion.div {...fadeUp} className="mt-10">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-white/10" data-testid={HOME_PAGE.faqItem(i)}>
                  <AccordionTrigger className="text-white text-left hover:no-underline text-base font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-zinc-400 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-6 lg:px-10">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center spotlight border border-white/10 rounded-sm p-14">
          <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight">Get 20+ Prompts That Actually Work</h2>
          <p className="text-zinc-400 mt-4 max-w-lg mx-auto">Proven prompt templates for emails, ads, social, and blogs — built from everything we've tested on this site.</p>
          <div className="mt-8 max-w-md mx-auto">
            <EmailOptInForm source="homepage_final_cta" compact />
          </div>
        </motion.div>
      </section>
    </>
  );
}
